import { useState } from "react";
import { useDrivers, useCrossCategory } from "../lib/realdata";
import type { ImportanceRow } from "../lib/realdata";

type Tab = "per_category" | "common" | "themes";
type Layer = "functional" | "emotional" | "brand";

export default function Drivers() {
  const drivers = useDrivers();
  const cross = useCrossCategory();

  const [tab, setTab] = useState<Tab>("per_category");
  const [layer, setLayer] = useState<Layer>("functional");

  if (!drivers || !cross) {
    return (
      <div className="loading-card">
        <div className="lab muted">Loading attribute drivers…</div>
      </div>
    );
  }

  const mgN = drivers.categories.MG.n;
  const fansN = drivers.categories.Fans.n;

  return (
    <div className="stagger">
      <section className="page-hero">
        <div className="kicker">v3 · Cross-category</div>
        <h1>What drives buyers across <em className="warn">Mixer Grinders</em> and <em className="warn">Fans</em></h1>
        <p className="muted">
          Importance ratings (A2a/A2b/A2c, scale 1-10) from {mgN.toLocaleString()} MG respondents and {fansN.toLocaleString()} Fans respondents.
          Bars show mean importance with 95% CI. Pulse compares the two studies side-by-side so a CMO can see which attributes
          to lead with category-by-category, and which travel across both.
        </p>
      </section>

      <section className="tabbar">
        <button className={"tab" + (tab === "per_category" ? " active" : "")} onClick={() => setTab("per_category")}>Per-category drivers</button>
        <button className={"tab" + (tab === "common" ? " active" : "")} onClick={() => setTab("common")}>Common drivers</button>
        <button className={"tab" + (tab === "themes" ? " active" : "")} onClick={() => setTab("themes")}>Functional themes</button>
      </section>

      {tab === "per_category" && (
        <PerCategoryView drivers={drivers} layer={layer} setLayer={setLayer} />
      )}
      {tab === "common" && (
        <CommonView cross={cross} />
      )}
      {tab === "themes" && (
        <ThemesView cross={cross} />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// Per-category drivers — bar charts side-by-side
// ----------------------------------------------------------------------
function PerCategoryView({ drivers, layer, setLayer }: {
  drivers: NonNullable<ReturnType<typeof useDrivers>>,
  layer: Layer, setLayer: (l: Layer) => void,
}) {
  const mg = drivers.categories.MG[layer];
  const fans = drivers.categories.Fans[layer];

  const layerLabels: Record<Layer, string> = {
    functional: "Functional attributes (A2a)",
    emotional: "Emotional attributes (A2b)",
    brand: "Brand attributes (A2c)",
  };

  const layerSubs: Record<Layer, string> = {
    functional: "What product features matter most to buyers in each category.",
    emotional: "How buyers want the product to make them feel.",
    brand: "What buyers want from the brand they choose.",
  };

  const mgTop = mg[0];
  const fansTop = fans[0];

  return (
    <>
      <section className="layer-toggle">
        {(["functional","emotional","brand"] as Layer[]).map((l) => (
          <button key={l}
            className={"chip" + (l === layer ? " active" : "")}
            onClick={() => setLayer(l)}>
            {l === "functional" ? "Functional" : l === "emotional" ? "Emotional" : "Brand"}
          </button>
        ))}
      </section>

      <section className="layer-head">
        <h2>{layerLabels[layer]}</h2>
        <p className="muted">{layerSubs[layer]}</p>
      </section>

      <section className="driver-grid">
        <DriverColumn title="Mixer Grinder" subject="MG" rows={mg} accent="teal" topInsight={
          mgTop ? `Top driver: "${mgTop.label}" (mean ${mgTop.mean?.toFixed(2)}, n=${mgTop.n})` : ""
        }/>
        <DriverColumn title="Fans" subject="Fans" rows={fans} accent="amber" topInsight={
          fansTop ? `Top driver: "${fansTop.label}" (mean ${fansTop.mean?.toFixed(2)}, n=${fansTop.n})` : ""
        }/>
      </section>
    </>
  );
}

function DriverColumn({ title, subject: _subject, rows, accent, topInsight }: {
  title: string; subject: string;
  rows: ImportanceRow[]; accent: "teal" | "amber";
  topInsight: string;
}) {
  const accentColor = accent === "teal" ? "var(--teal)" : "var(--amber)";
  const accentSoft = accent === "teal" ? "var(--teal-tint)" : "var(--amber-soft)";
  const min = 6.5; // visible bar baseline so the spread is readable
  const max = 9;
  const range = max - min;

  return (
    <div className="driver-col">
      <div className="driver-head">
        <h3>{title}</h3>
        <span className="muted">n={rows[0]?.n.toLocaleString() ?? "-"}</span>
      </div>
      <div className="muted topinsight">{topInsight}</div>
      <div className="driver-bars">
        {rows.map((r) => {
          const m = r.mean ?? 0;
          const lo = r.ci_low ?? 0;
          const hi = r.ci_high ?? 0;
          const wPct = Math.max(0, Math.min(100, ((m - min) / range) * 100));
          const loPct = Math.max(0, Math.min(100, ((lo - min) / range) * 100));
          const hiPct = Math.max(0, Math.min(100, ((hi - min) / range) * 100));
          return (
            <div key={r.code} className="dr-row">
              <div className="dr-label" title={r.label}>{r.label}</div>
              <div className="dr-track" style={{ background: accentSoft }}>
                <div className="dr-fill" style={{ width: `${wPct}%`, background: accentColor }} />
                {hi > lo && (
                  <div className="dr-ci" style={{
                    left: `${loPct}%`,
                    width: `${Math.max(1, hiPct - loPct)}%`,
                  }} />
                )}
              </div>
              <div className="dr-val">{r.mean?.toFixed(2) ?? "-"}</div>
            </div>
          );
        })}
      </div>
      <div className="driver-foot muted">Scale: 1 = least important, 10 = most important. Whiskers = 95% CI.</div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Common drivers — scatter plot (MG x Fans on emotional + brand statements)
// ----------------------------------------------------------------------
function CommonView({ cross }: { cross: NonNullable<ReturnType<typeof useCrossCategory>> }) {
  const [layer, setLayer] = useState<"emotional" | "brand">("brand");
  const pairs = layer === "brand" ? cross.common_brand : cross.common_emotional;

  // Quadrant context: top-right = high in both, bottom-left = low in both
  const W = 720, H = 480, P = 56;
  const xLo = 7.5, xHi = 8.6;
  const yLo = 7.5, yHi = 8.6;
  const x = (v: number) => P + ((v - xLo) / (xHi - xLo)) * (W - 2 * P);
  const y = (v: number) => H - P - ((v - yLo) / (yHi - yLo)) * (H - 2 * P);

  // Median lines
  const xMed = pairs.reduce((s, p) => s + p.mg_mean, 0) / pairs.length;
  const yMed = pairs.reduce((s, p) => s + p.fans_mean, 0) / pairs.length;

  return (
    <>
      <section className="layer-toggle">
        <button className={"chip" + (layer === "brand" ? " active" : "")} onClick={() => setLayer("brand")}>Brand attributes</button>
        <button className={"chip" + (layer === "emotional" ? " active" : "")} onClick={() => setLayer("emotional")}>Emotional attributes</button>
      </section>

      <section className="layer-head">
        <h2>Where MG and Fans agree</h2>
        <p className="muted">
          Each dot is a {layer === "brand" ? "brand" : "emotional"} statement. X = importance to MG buyers, Y = importance to Fans buyers.
          The top-right quadrant is the common-driver zone - these are the attributes that
          travel across both categories and warrant enterprise-level marketing investment.
        </p>
      </section>

      <section className="scatter-card">
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxHeight: 520 }}>
          <rect x={P} y={P} width={W - 2 * P} height={H - 2 * P} fill="var(--card-2)" stroke="var(--line)" />
          {/* Median lines */}
          <line x1={x(xMed)} y1={P} x2={x(xMed)} y2={H - P} stroke="var(--ink-20)" strokeDasharray="4 4" />
          <line x1={P} y1={y(yMed)} x2={W - P} y2={y(yMed)} stroke="var(--ink-20)" strokeDasharray="4 4" />
          {/* Quadrant labels */}
          <text x={W - P - 6} y={P + 16} textAnchor="end" fontSize="11" fill="var(--teal)">Important in BOTH</text>
          <text x={P + 6} y={P + 16} fontSize="11" fill="var(--ink-40)">Fans-led</text>
          <text x={W - P - 6} y={H - P - 6} textAnchor="end" fontSize="11" fill="var(--ink-40)">MG-led</text>
          <text x={P + 6} y={H - P - 6} fontSize="11" fill="var(--ink-40)">Lower in both</text>

          {/* Axis ticks */}
          {[7.5, 8.0, 8.5].map((t) => (
            <g key={`x${t}`}>
              <line x1={x(t)} y1={H - P} x2={x(t)} y2={H - P + 4} stroke="var(--ink-20)" />
              <text x={x(t)} y={H - P + 16} textAnchor="middle" fontSize="10" fill="var(--ink-60)">{t.toFixed(1)}</text>
            </g>
          ))}
          {[7.5, 8.0, 8.5].map((t) => (
            <g key={`y${t}`}>
              <line x1={P - 4} y1={y(t)} x2={P} y2={y(t)} stroke="var(--ink-20)" />
              <text x={P - 8} y={y(t) + 3} textAnchor="end" fontSize="10" fill="var(--ink-60)">{t.toFixed(1)}</text>
            </g>
          ))}
          <text x={W / 2} y={H - 12} textAnchor="middle" fontSize="11" fill="var(--ink-60)">Importance to MG buyers →</text>
          <text x={14} y={H / 2} textAnchor="middle" fontSize="11" fill="var(--ink-60)" transform={`rotate(-90 14 ${H / 2})`}>Importance to Fans buyers →</text>

          {/* Dots */}
          {pairs.map((p) => {
            const isCommon = p.mg_mean >= xMed && p.fans_mean >= yMed;
            return (
              <g key={p.label}>
                <circle cx={x(p.mg_mean)} cy={y(p.fans_mean)} r={isCommon ? 8 : 6}
                  fill={isCommon ? "var(--teal)" : "var(--ink-40)"}
                  fillOpacity={0.85} stroke="white" strokeWidth="1.5" />
                <text x={x(p.mg_mean) + 10} y={y(p.fans_mean) + 4} fontSize="10.5"
                  fill={isCommon ? "var(--teal-ink)" : "var(--ink-2)"}
                  fontWeight={isCommon ? 600 : 400}>
                  {p.label}
                </text>
              </g>
            );
          })}
        </svg>
        <div className="muted scatter-foot">
          Solid teal dots are above the median on BOTH axes - the common-driver zone. Dashed lines mark the medians ({xMed.toFixed(2)}, {yMed.toFixed(2)}).
        </div>
      </section>

      <section className="common-table-card">
        <h3>Statement-by-statement comparison</h3>
        <table className="cmp-table">
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Statement</th>
              <th>MG mean</th>
              <th>Fans mean</th>
              <th>Δ</th>
              <th>Bias</th>
            </tr>
          </thead>
          <tbody>
            {pairs.slice().sort((a, b) =>
              ((b.mg_mean + b.fans_mean) / 2) - ((a.mg_mean + a.fans_mean) / 2)
            ).map((p) => {
              const d = p.fans_mean - p.mg_mean;
              const bias = Math.abs(d) < 0.05 ? "—" : d > 0 ? "Fans" : "MG";
              const sign = d > 0 ? "+" : "";
              return (
                <tr key={p.label}>
                  <td style={{ textAlign: "left" }}>{p.label}</td>
                  <td>{p.mg_mean.toFixed(2)}</td>
                  <td>{p.fans_mean.toFixed(2)}</td>
                  <td className={d > 0 ? "pos" : d < 0 ? "neg" : ""}>{sign}{d.toFixed(2)}</td>
                  <td><span className={"pill " + (bias === "Fans" ? "amber" : bias === "MG" ? "teal" : "neutral")}>{bias}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </>
  );
}

// ----------------------------------------------------------------------
// Functional themes — paired bars (MG vs Fans) per theme
// ----------------------------------------------------------------------
function ThemesView({ cross }: { cross: NonNullable<ReturnType<typeof useCrossCategory>> }) {
  const themes = cross.functional_themes;
  const min = 7.5, max = 8.6, range = max - min;

  return (
    <>
      <section className="layer-head">
        <h2>Functional themes that span both categories</h2>
        <p className="muted">
          MG and Fans have category-specific functional statements (a fan can't be "rightly priced" the same way a grinder is "easy to clean"),
          so we roll statements up into themes a marketer can actually act on. Audit trail visible per theme.
        </p>
      </section>

      <section className="theme-grid">
        {themes.map((t) => {
          const mg = t.mg_mean ?? 0;
          const fa = t.fans_mean ?? 0;
          const mgPct = ((mg - min) / range) * 100;
          const faPct = ((fa - min) / range) * 100;
          return (
            <div key={t.id} className="theme-card">
              <div className="theme-hd">
                <h3>{t.label}</h3>
                <div className="theme-score">{t.common_score?.toFixed(2) ?? "-"}</div>
              </div>
              <div className="theme-row">
                <div className="bar-track teal-track"><div className="bar-fill teal-fill" style={{ width: `${mgPct}%` }} /></div>
                <span className="ttag teal">MG · {mg.toFixed(2)}</span>
              </div>
              <div className="theme-row">
                <div className="bar-track amber-track"><div className="bar-fill amber-fill" style={{ width: `${faPct}%` }} /></div>
                <span className="ttag amber">Fans · {fa.toFixed(2)}</span>
              </div>
              <div className="theme-codes muted">
                MG codes: {t.mg_codes.join(", ")} · Fans codes: {t.fans_codes.join(", ")}
              </div>
            </div>
          );
        })}
      </section>
    </>
  );
}
